import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, Search, BarChart3, Target, Globe2, 
  ArrowUp, ArrowDown, Minus, RefreshCw, Upload, Plus,
  LineChart, AlertCircle, CheckCircle, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketIntelligence() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('trends');
  
  // Trends state
  const [keywords, setKeywords] = useState('');
  const [region, setRegion] = useState('');
  const [trendsData, setTrendsData] = useState(null);
  
  // MOI state
  const [moiResult, setMoiResult] = useState(null);
  const [moiLoading, setMoiLoading] = useState(false);
  
  // Reference data
  const [verticals, setVerticals] = useState([]);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [verticalsRes, regionsRes] = await Promise.all([
        fetch(`${API_URL}/api/gois/intelligence/verticals`),
        fetch(`${API_URL}/api/gois/intelligence/regions`)
      ]);
      
      if (verticalsRes.ok) {
        const data = await verticalsRes.json();
        setVerticals(data.verticals || []);
      }
      if (regionsRes.ok) {
        const data = await regionsRes.json();
        setRegions(data.regions || []);
      }
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const handleSearchTrends = async () => {
    if (!keywords.trim()) {
      toast.error('Please enter at least one keyword');
      return;
    }
    
    setLoading(true);
    try {
      const keywordList = keywords.split(',').map(k => k.trim()).filter(k => k).slice(0, 5);
      
      const response = await fetch(`${API_URL}/api/gois/intelligence/trends`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keywords: keywordList,
          region: region,
          timeframe: 'today 3-m'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrendsData(data.data);
        toast.success('Trends data retrieved successfully');
      } else {
        toast.error('Failed to fetch trends data');
      }
    } catch (error) {
      toast.error('Failed to fetch trends data');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateMOI = async () => {
    setMoiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gois/intelligence/moi`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: 'demo_client',
          market_data: {
            search_interest_index: trendsData?.data?.[0]?.average || 55,
            estimated_impressions: 350000,
            competition_score: 42,
            avg_cpc: 2.8,
            avg_ctr: 2.5,
            estimated_market_spend: 80000,
            trend_direction: trendsData?.data?.[0]?.trend || 'stable',
            trend_values: trendsData?.data?.[0]?.values || [50, 52, 54, 56, 58]
          },
          campaign_data: {
            spend: 22000,
            cpc: 2.4,
            ctr: 2.9
          },
          vertical: 'ecommerce',
          region: region || 'US'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMoiResult(data);
        toast.success('MOI calculated successfully');
      } else {
        toast.error('Failed to calculate MOI');
      }
    } catch (error) {
      toast.error('Failed to calculate MOI');
    } finally {
      setMoiLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'rising': return <ArrowUp className="h-4 w-4 text-green-400" />;
      case 'declining': return <ArrowDown className="h-4 w-4 text-red-400" />;
      default: return <Minus className="h-4 w-4 text-zinc-400" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'rising': return 'text-green-400';
      case 'declining': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  const getMOIColor = (classification) => {
    switch (classification?.level) {
      case 'high_expansion': return 'text-green-400';
      case 'optimization': return 'text-blue-400';
      case 'balanced': return 'text-[#D4A84B]';
      case 'structural_risk': return 'text-orange-400';
      case 'reconsideration': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="market-intelligence">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-[#D4A84B]" />
            Market Intelligence
          </h1>
          <p className="text-zinc-400 mt-1">
            GIOS Intelligence Layer - Market trends, opportunity scoring, and gap analysis
          </p>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="trends" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              <LineChart className="h-4 w-4 mr-2" />
              Trends Analysis
            </TabsTrigger>
            <TabsTrigger value="moi" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              <Target className="h-4 w-4 mr-2" />
              MOI Calculator
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-[#D4A84B] data-[state=active]:text-black">
              <Upload className="h-4 w-4 mr-2" />
              Data Upload
            </TabsTrigger>
          </TabsList>

          {/* Trends Analysis Tab */}
          <TabsContent value="trends" className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="h-5 w-5 text-[#D4A84B]" />
                  Google Trends Analysis
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Analyze search interest for keywords across markets
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="text-zinc-400 text-sm block mb-2">Keywords (comma-separated, max 5)</label>
                    <Input
                      placeholder="digital marketing, seo, ppc"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div className="w-48">
                    <label className="text-zinc-400 text-sm block mb-2">Region</label>
                    <select
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      className="w-full h-10 px-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                    >
                      <option value="">Global</option>
                      {regions.map(r => (
                        <option key={r.code} value={r.code}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleSearchTrends}
                      disabled={loading}
                      className="bg-[#D4A84B] hover:bg-[#c99b3f] text-black"
                    >
                      {loading ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                      <span className="ml-2">Analyze</span>
                    </Button>
                  </div>
                </div>

                {/* Trends Results */}
                {trendsData && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-medium">Results</h3>
                      <Badge variant="outline" className="border-[#D4A84B]/30 text-[#D4A84B]">
                        {trendsData.status === 'mock' ? 'Mock Data' : 'Live Data'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(trendsData.data || trendsData.mock_data?.data || []).map((item, index) => (
                        <Card key={index} className="bg-zinc-800/50 border-zinc-700">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="text-white font-medium truncate">{item.keyword}</h4>
                              <div className={`flex items-center gap-1 ${getTrendColor(item.trend)}`}>
                                {getTrendIcon(item.trend)}
                                <span className="text-sm capitalize">{item.trend}</span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-zinc-500">Average</p>
                                <p className="text-white font-medium">{Math.round(item.average)}</p>
                              </div>
                              <div>
                                <p className="text-zinc-500">Peak</p>
                                <p className="text-white font-medium">{item.peak}</p>
                              </div>
                            </div>

                            {/* Mini sparkline visualization */}
                            <div className="mt-3 flex items-end gap-0.5 h-12">
                              {(item.values || []).slice(-12).map((val, i) => (
                                <div 
                                  key={i}
                                  className="flex-1 bg-[#D4A84B]/60 rounded-t"
                                  style={{ height: `${(val / 100) * 100}%` }}
                                />
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MOI Calculator Tab */}
          <TabsContent value="moi" className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#D4A84B]" />
                  MediaKshetra Opportunity Index (MOI)
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Proprietary scoring for market opportunity assessment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="bg-zinc-800/50 p-4 rounded-lg">
                    <p className="text-zinc-300 text-sm">
                      The MOI score evaluates market opportunity based on demand signals, competition analysis, 
                      efficiency metrics, and compliance factors. Higher scores indicate stronger expansion opportunities.
                    </p>
                  </div>

                  {trendsData ? (
                    <Button
                      onClick={handleCalculateMOI}
                      disabled={moiLoading}
                      className="bg-[#D4A84B] hover:bg-[#c99b3f] text-black"
                    >
                      {moiLoading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <BarChart3 className="h-4 w-4 mr-2" />
                      )}
                      Calculate MOI Score
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 text-zinc-400">
                      <AlertCircle className="h-5 w-5" />
                      <span>Run a trends analysis first to populate market data</span>
                    </div>
                  )}

                  {/* MOI Result */}
                  {moiResult && (
                    <div className="mt-6 p-6 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <p className="text-zinc-400 text-sm">Opportunity Index</p>
                          <p className={`text-5xl font-bold ${getMOIColor(moiResult.classification)}`}>
                            {moiResult.moi_score}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge 
                            className="text-lg px-4 py-2"
                            style={{ 
                              backgroundColor: moiResult.classification?.color + '20', 
                              color: moiResult.classification?.color,
                              border: `1px solid ${moiResult.classification?.color}40`
                            }}
                          >
                            {moiResult.classification?.label}
                          </Badge>
                          <p className="text-zinc-400 text-sm mt-2">
                            {moiResult.classification?.action}
                          </p>
                        </div>
                      </div>

                      {/* Score Interpretation */}
                      <div className="grid grid-cols-5 gap-2 mb-6">
                        {[
                          { min: 80, max: 100, label: 'High Expansion', color: '#22c55e' },
                          { min: 60, max: 79, label: 'Optimization', color: '#3b82f6' },
                          { min: 40, max: 59, label: 'Balanced', color: '#D4A84B' },
                          { min: 20, max: 39, label: 'Structural Risk', color: '#f97316' },
                          { min: 0, max: 19, label: 'Reconsideration', color: '#ef4444' }
                        ].map((tier, i) => (
                          <div 
                            key={i} 
                            className={`p-2 rounded text-center text-xs ${
                              moiResult.moi_score >= tier.min && moiResult.moi_score <= tier.max 
                                ? 'ring-2 ring-white' 
                                : 'opacity-50'
                            }`}
                            style={{ backgroundColor: tier.color + '20', color: tier.color }}
                          >
                            <p className="font-medium">{tier.min}-{tier.max}</p>
                            <p className="mt-1">{tier.label}</p>
                          </div>
                        ))}
                      </div>

                      {/* Recommendations */}
                      <div>
                        <p className="text-zinc-400 text-sm mb-3">Recommendations</p>
                        <div className="space-y-2">
                          {moiResult.recommendations?.map((rec, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Zap className="h-4 w-4 mt-0.5 text-[#D4A84B]" />
                              <span className="text-zinc-300 text-sm">{rec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* MOI Classification Legend */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">MOI Classification Scale</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { range: '80-100', label: 'High Expansion Opportunity', desc: 'Aggressive scaling recommended', color: '#22c55e' },
                    { range: '60-79', label: 'Optimization Zone', desc: 'Focus on efficiency improvements', color: '#3b82f6' },
                    { range: '40-59', label: 'Balanced Performance', desc: 'Maintain current strategy with incremental optimization', color: '#D4A84B' },
                    { range: '20-39', label: 'Structural Risk', desc: 'Review fundamentals and market positioning', color: '#f97316' },
                    { range: '<20', label: 'Strategic Reconsideration', desc: 'Major pivot or exit recommended', color: '#ef4444' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg">
                      <div 
                        className="w-16 h-8 rounded flex items-center justify-center font-bold"
                        style={{ backgroundColor: item.color + '20', color: item.color }}
                      >
                        {item.range}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.label}</p>
                        <p className="text-zinc-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Upload Tab */}
          <TabsContent value="upload" className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Upload className="h-5 w-5 text-[#D4A84B]" />
                  Manual Data Upload
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Upload campaign performance data when API connections are not available
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Upload className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-medium mb-2">Upload Campaign Data</h3>
                  <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                    Import your campaign performance data (impressions, clicks, spend, conversions) 
                    for MOI calculation when platform APIs are not connected.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" className="border-zinc-700 text-zinc-300">
                      Download Template
                    </Button>
                    <Button className="bg-[#D4A84B] hover:bg-[#c99b3f] text-black">
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Supported Data Types */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { type: 'Impressions', desc: 'Ad view counts by period', icon: Globe2 },
                { type: 'Clicks', desc: 'Click-through data', icon: Target },
                { type: 'Spend', desc: 'Budget utilization', icon: BarChart3 },
                { type: 'Conversions', desc: 'Goal completions', icon: CheckCircle }
              ].map((item, i) => (
                <Card key={i} className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="pt-6">
                    <item.icon className="h-8 w-8 text-[#D4A84B] mb-3" />
                    <h4 className="text-white font-medium">{item.type}</h4>
                    <p className="text-zinc-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Verticals Reference */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Supported Industry Verticals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {verticals.map(v => (
                <Badge 
                  key={v.id} 
                  variant="outline" 
                  className="border-zinc-700 text-zinc-300"
                  title={`Sensitivity factor: ${v.sensitivity}`}
                >
                  {v.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
